import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  DataRoomNode,
  DataRoomNodeTag,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import DataRoomNodeTagAndVersion from 'components/DataRoomNodeTagAndVersion';
import DataRoomDocumentsContext from '../../providers/DataRoomDocuments/DataRoomDocuments.context';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox } from 'ncoded-component-library';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import EditIcon from 'icons/Pencil.icon';
import utils, {
  getNodeMimeType,
  getIsRevisedVersion,
  displayDataRoomNodeSize,
  getFileIdsForShareableLinks,
  hasNotSeenDiscoveryFeature,
} from 'utils';
import { isDateFromLastDays } from 'dateUtils';
import useNumberOfMembersWithAccess from 'hooks/useNumberOfMembersWithAccess';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DotshorizontalIcon from 'icons/Dotshorizontal.icon';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { RenameModal } from '../DataRoomDocumentDrawers/RenameModal';
import useDeleteFile from '../../hooks/useDeleteFile';
import { MoveDocument } from '../DataRoomDocumentDrawers/MoveDocument';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { MoveDocumentRef } from '../DataRoomDocumentDrawers/MoveDocument/MoveDocument.component';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import TruckIcon from 'icons/Truck.icon';
import { RightClickDropdown } from 'components/RightClickDropdown';
import useMatchMedia from 'hooks/useMatchMedia';
import useMobileLongClick from 'hooks/useMobileLongClick';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import MymeTypeIcon from '../MymeTypeIcon';
import MessageIcon from 'icons/Message.icon';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import ResolvePopup from 'components/ResolvePopup';
import DownloadIcon from 'icons/Download.icon';
import ProductsIcon from 'icons/Products.icon';
import DrNodeTagDrawer from '../DrNodeTagDrawer';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { EditableDocument } from './components/EditableDocument';
import LockopenIcon from 'icons/Lockopen.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import ArrowClockIcon from 'icons/ArrowClock.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { isDataRoomViewer } from 'router/subrouters/DataRoom/utils';
import useReplaceFile from '../../hooks/useReplaceFile';
import usePastTime from 'hooks/usePastTime';
import { ALIGNMENT } from 'models/Global';
import TagDocumentBanner from '../TagDocumentBanner';
import { TRANSITION_TIME } from 'data/global';
import ManageFileAccess from '../DataRoomDocumentDrawers/ManageFileAccess';
import NewDocumentTag from '../NewDocumentTag';
import useDataRoomDocument from 'hooks/useDataRoomDocument';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import DataRoomDocumentMobileModal from './components/DataRoomDocumentMobileModal';
import DesktopMemberAccess from './components/DesktopMemberAccess';
import useInsights from './hooks/useInsights';
import LinkIcon from 'icons/Link.icon';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import XcircleIcon from 'icons/Xcircle.icon';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import StarIcon from 'icons/Star.icon';
import { getAxiosErrorMessageCode } from 'uploadFileUtils';
import { ERROR_TYPES } from 'request-responses';
import { AxiosError } from 'axios';
import DiscoveringFeature from 'components/DiscoveringFeature';
import { FIRST_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';

import './DataRoomDocument.styles.scss';
import './DataRoomDocument.styles.responsive.scss';

const NAME_ID = 'name';

type DataRoomNodeProps = {
  className?: string;
  node: DataRoomNode;
  isUploading: boolean;
  suggestedTag?: DataRoomNodeTag;
  isRequestPending: boolean;
  isSelected?: boolean;
  isSelectedMode?: boolean;
  areInsightsShown: boolean;
  canEnterEditMode?: boolean;
  dropdownOptions?: any[];
  canTagFiles: boolean;
  index?: number;
  updateNode: (node: DataRoomNode) => void;
  onDeleteDocument: (newState: DataRoomNode[]) => void;
  onTagSet: (node: DataRoomNode) => void;
  removeTagFromOldNode: (nodeId: string) => void;
  setIsRequestPending: (val: boolean) => void;
  onManageDocument?: () => void;
  onQandA?: () => void;
  onSelect?: (node: DataRoomNode) => void;
  setIsDragging?: (value: boolean) => void;
  onDownload: (node: DataRoomNode) => void;
  onViewFileModalOpen?: () => void;
  onUnstarDocuments: () => void;
};

const DataRoomDocument: React.FC<DataRoomNodeProps> = (props) => {
  const {
    className,
    node,
    isRequestPending,
    areInsightsShown,
    canTagFiles,
    suggestedTag,
    dropdownOptions: dropdownOptionsProps,
    isUploading,
    index,
    canEnterEditMode = false,
    isSelected = false,
    isSelectedMode = false,
    onSelect,
    onQandA,
    setIsRequestPending,
    setIsDragging,
    onDownload,
    onTagSet,
    removeTagFromOldNode,
    updateNode,
    onViewFileModalOpen,
    onUnstarDocuments,
  } = props;

  const {
    _id,
    name,
    createdAt,
    modifiedAt,
    numberOfMembersWithAccess,
    document,
    hasAuthorizedActions,
    numberOfDownloads,
    numberOfViews,
    buyerNumberOfViews,
    buyerNumberOfDownloads,
    tag,
    downloadSecurePdf,
    version,
    docIndex,
    hasInvalidDocIndex,
    viewedDate,
    directChildrenIds,
    isProcessedSuccessfully,
    failedConversionToPdf,
    hasFilesFailedConversionToPdf,
    userId,
    isStarred,
  } = node;

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarring, setIsStarring] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    currentUser: { _id: currentUserId, seenStarringFilesFeature },
  } = useContext(CurrentUserContext);
  const { openNode } = useContext(DocumentViewContext);
  const {
    baseDocsPath,
    buySidePreviewMember,
    setCurrentlySelectedFolder,
    setCurrentlyDisplayedNodes,
    closeDiscoveringFeature,
  } = useContext(DataRoomDocumentsContext);
  const {
    canBuyerAskQuestion,
    isAdmin,
    isBuySide,
    hasIndexGenerated,
    doesntHaveAccess,
    canUserCreateShareableLinks,
    qaSide,
    dataRoom: { name: dataRoomName, userId: ownerId },
  } = useContext(DataRoomContext);
  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);

  const isPhablet = useMatchMedia('isPhablet');

  const {
    isFile,
    isFolder,
    isFolderEmpty,
    isVideoFile,
    nodeSize,
    canDownload,
    canShareNode,
  } = useDataRoomDocument(node);

  const isMyNode = useMemo(
    () => userId === currentUserId,
    [currentUserId, userId],
  );

  const showNodeStatusBadge = useMemo(
    () =>
      (isAdmin || isMyNode) &&
      ((isFolder && hasFilesFailedConversionToPdf) ||
        (isFile && (!isProcessedSuccessfully || failedConversionToPdf))),
    [
      failedConversionToPdf,
      hasFilesFailedConversionToPdf,
      isAdmin,
      isFile,
      isFolder,
      isMyNode,
      isProcessedSuccessfully,
    ],
  );

  const isInViewAsMode = !!buySidePreviewMember;
  const isUserAuthorized = !isInViewAsMode && hasAuthorizedActions;

  const isEditable =
    !isLoading && !isPhablet && canEnterEditMode && isUserAuthorized;

  const timeoutRef = useRef(null);

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const [phoneModal, phoneModalRef] = useCallbackRef<ModalRef>(null);
  const [renameModal, renameModalRef] = useCallbackRef<OverlayRef>(null);
  const [moveTheFile, moveTheFileRef] = useCallbackRef<MoveDocumentRef | null>(
    null,
  );
  const [tagThisFileDrawer, tagThisFileDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [manageFilesAccess, manageFilesAccessRef] =
    useCallbackRef<SideDrawerRef | null>(null);

  const showNewTag = useMemo(() => {
    if (!isBuySide || viewedDate || isFolderEmpty) return false;

    return isDateFromLastDays(createdAt, 30);
  }, [createdAt, isBuySide, isFolderEmpty, viewedDate]);

  const classes = classNames(
    'data-room-document',
    {
      'new-document': showNewTag,
      editing: isEditing,
      'selected-mode': isSelectedMode,
      selected: isSelected,
    },
    className,
  );

  const isBuySidePreviewMemberViewer = useMemo(
    () => isDataRoomViewer(buySidePreviewMember?.role),
    [buySidePreviewMember],
  );

  const onMouseDown = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDragging?.(true);
    }, TRANSITION_TIME);
  };

  const onMouseUp = () => {
    clearTimeout(timeoutRef.current);
  };

  const { t } = useTranslation();

  const onDocDelete = useDeleteFile();

  const navigate = useNavigate();

  const pastTime = usePastTime();

  const numberOfMembersWithAccessCopy = useNumberOfMembersWithAccess()(
    numberOfMembersWithAccess,
  );

  const { handleTouchEnd, handleTouchStart } = useMobileLongClick({
    onSelect: () => onSelect(node),
  });

  const { restOfRootProps, getInputProps, onReplaceClick } =
    useReplaceFile(node);

  const copyToClipboard = useCopyToClipboard();

  const onChangeSecureDownload = useCallback(async () => {
    try {
      const updatedDownloadSecurePdf = !downloadSecurePdf;

      await dataRoomApi.nodes.changeIsSecuredDownload(
        _id,
        updatedDownloadSecurePdf,
      );

      updateNode({ ...node, downloadSecurePdf: updatedDownloadSecurePdf });

      popSuccess(
        t(
          updatedDownloadSecurePdf
            ? 'enableRemoteTerminationSuccess'
            : 'disableRemoteTerminationSucceess',
          { fileName: node.name },
        ),
      );
    } catch (e) {
      popServerError(e);
    }
  }, [_id, downloadSecurePdf, node, popServerError, popSuccess, t, updateNode]);

  const openTagNodeDrawer = useCallback(
    () => tagThisFileDrawer.setOpen(true),
    [tagThisFileDrawer],
  );

  const onSecureShare = useCallback(
    () => openCreateShareableLink(getFileIdsForShareableLinks([node])),
    [node, openCreateShareableLink],
  );

  const dropdownOptions = useMemo(
    () =>
      doesntHaveAccess
        ? []
        : dropdownOptionsProps || [
            ...(canBuyerAskQuestion && !isFolder
              ? [
                  {
                    label: t('askAQuestion'),
                    onClick: onQandA,
                    icon: <MessageIcon />,
                  },
                ]
              : []),
            ...(canDownload
              ? [
                  {
                    label: t('download'),
                    onClick: () => onDownload(node),
                    icon: <DownloadIcon />,
                  },
                ]
              : []),
            ...(isUserAuthorized
              ? [
                  ...(canShareNode
                    ? [
                        {
                          label: t('shareFile'),
                          onClick: () => manageFilesAccess?.setOpen(true),
                          icon: <GroupUsersIcon strokeWidth={2} />,
                        },
                        ...(canUserCreateShareableLinks
                          ? [
                              {
                                label: t('createSecureLink'),
                                onClick: onSecureShare,
                                icon: <LinkIcon />,
                              },
                            ]
                          : []),
                      ]
                    : []),
                  {
                    label: t('move'),
                    onClick: moveTheFile?.open,
                    icon: <TruckIcon />,
                  },
                  {
                    label: t('rename'),
                    onClick: renameModal?.open,
                    icon: <EditIcon />,
                  },
                  ...(canTagFiles && !isFolder
                    ? [
                        {
                          label: t('tagThisFile'),
                          onClick: openTagNodeDrawer,
                          icon: <ProductsIcon />,
                        },
                      ]
                    : []),
                  ...(!isFolder
                    ? [
                        ...(!isVideoFile
                          ? [
                              {
                                label: t(
                                  downloadSecurePdf
                                    ? 'disableRemoteTermination'
                                    : 'enableRemoteTermination',
                                ),
                                onClick: onChangeSecureDownload,
                                icon: downloadSecurePdf ? (
                                  <LockopenIcon />
                                ) : (
                                  <LockclosedIcon />
                                ),
                              },
                            ]
                          : []),
                        {
                          label: t('addNewVersion'),
                          onClick: onReplaceClick,
                          icon: <ArrowClockIcon />,
                        },
                      ]
                    : []),
                  {
                    label: t('delete'),
                    onClick: () =>
                      onDocDelete([node], setIsRequestPending, true),
                    icon: <TrashIcon />,
                    alert: true,
                  },
                ]
              : []),
          ],
    [
      doesntHaveAccess,
      dropdownOptionsProps,
      canBuyerAskQuestion,
      isFolder,
      isVideoFile,
      node,
      isUserAuthorized,
      canShareNode,
      canTagFiles,
      downloadSecurePdf,
      manageFilesAccess,
      canDownload,
      canUserCreateShareableLinks,
      moveTheFile?.open,
      renameModal?.open,
      t,
      onQandA,
      onChangeSecureDownload,
      onDownload,
      openTagNodeDrawer,
      onReplaceClick,
      onDocDelete,
      setIsRequestPending,
      onSecureShare,
    ],
  );

  const showMenu = useMemo(
    () => !isUploading && !!dropdownOptions.length && !isInViewAsMode,
    [isUploading, dropdownOptions.length, isInViewAsMode],
  );

  const nodes = useMemo(() => [node], [node]);

  const rightClickDisabled =
    isUploading ||
    !showMenu ||
    isEditing ||
    dropdown?.isOpen ||
    moveTheFile?.isOpen ||
    manageFilesAccess?.open ||
    tagThisFileDrawer?.open;

  const showMobileOptions =
    hasAuthorizedActions || canDownload || (canBuyerAskQuestion && !isFolder);

  const { downloadInsight, viewInsight } = useInsights(
    buySidePreviewMember,
    isBuySidePreviewMemberViewer,
    numberOfViews,
    numberOfDownloads,
    buyerNumberOfViews,
    buyerNumberOfDownloads,
  );

  const handleOpenFile = useCallback(() => {
    if (doesntHaveAccess || !_id) return;

    openNode(node, { dataRoomName, userSide: qaSide, ownerId });
    updateNode({ ...node, viewedDate: new Date() });
    onViewFileModalOpen?.();
  }, [
    _id,
    node,
    doesntHaveAccess,
    dataRoomName,
    ownerId,
    qaSide,
    onViewFileModalOpen,
    openNode,
    updateNode,
  ]);

  const handleOpenFolder = useCallback(() => {
    setCurrentlySelectedFolder(node);
    navigate(`${baseDocsPath}${_id}`);
  }, [baseDocsPath, node, _id, navigate, setCurrentlySelectedFolder]);

  const handleOpen = isFolder ? handleOpenFolder : handleOpenFile;

  const handleStarDocument = useCallback(async () => {
    try {
      closeDiscoveringFeature();
      setIsStarring(true);
      const newIsStarred = !isStarred;

      await dataRoomApi.nodes[newIsStarred ? 'starDocument' : 'unStarDocument'](
        _id,
      );

      updateNode({ ...node, isStarred: newIsStarred });
    } catch (e) {
      const errorCode = getAxiosErrorMessageCode(e as AxiosError);

      if (errorCode === ERROR_TYPES.DATA_ROOM_STARRED_FILES_LIMIT_EXCEEDED)
        onUnstarDocuments();
      else popServerError(e);
    } finally {
      setIsStarring(false);
    }
  }, [
    isStarred,
    _id,
    node,
    updateNode,
    onUnstarDocuments,
    popServerError,
    closeDiscoveringFeature,
  ]);

  const hasNewStarringFeature =
    isPhablet &&
    index === 0 &&
    hasNotSeenDiscoveryFeature(
      seenStarringFilesFeature,
      FIRST_RELEASE_DISCOVERING_FEATURE,
    );

  const RightSideMenu = useMemo(
    () => (
      <span className="right-menu">
        {isAdmin &&
          !isFolder &&
          !buySidePreviewMember &&
          (hasNewStarringFeature ? (
            <DiscoveringFeature
              showBannerNotification
              createdAt={FIRST_RELEASE_DISCOVERING_FEATURE}
              title={t('starFilesForQuickSharing')}
              description={t('starFilesForQuickSharingDescription')}
              onClose={closeDiscoveringFeature}
              anchor={
                <Button
                  variant="icon"
                  disabled={isStarring}
                  className={classNames('star-document', 'new-feature', {
                    starred: isStarred,
                  })}
                  onClick={handleStarDocument}
                >
                  <StarIcon />
                </Button>
              }
            />
          ) : (
            <Button
              variant="icon"
              disabled={isStarring}
              className={classNames('star-document', { starred: isStarred })}
              onClick={handleStarDocument}
            >
              <StarIcon />
            </Button>
          ))}

        {!isFolder && areInsightsShown && (isAdmin || buySidePreviewMember) && (
          <>
            {downloadInsight}
            {viewInsight}
          </>
        )}

        {isBuySide &&
          (viewedDate ? (
            <span>{pastTime(viewedDate)}</span>
          ) : (
            showNewTag && (
              <NewDocumentTag
                {...(getIsRevisedVersion(version) && {
                  tooltip: t('revisedVersionOfFile'),
                })}
              />
            )
          ))}

        {buySidePreviewMember ? (
          <div className="member-role">
            {isBuySidePreviewMemberViewer ? 'view' : 'download'}
          </div>
        ) : (
          <DesktopMemberAccess
            node={node}
            isAdmin={isAdmin}
            isBuySide={isBuySide}
            decrementNumberOfMemberAccess={() =>
              updateNode({
                ...node,
                numberOfMembersWithAccess: node.numberOfMembersWithAccess - 1,
              })
            }
          />
        )}

        <span className="direct-children">
          {isFolder ? directChildrenIds?.length || 0 : ''}
        </span>

        <span>{displayDataRoomNodeSize(nodeSize)}</span>

        <span>{pastTime(modifiedAt)}</span>

        <span className="mobile-menu-open">
          {!doesntHaveAccess && showMobileOptions && !isSelectedMode && (
            <Button onClick={phoneModal?.open} icon={DotshorizontalIcon} />
          )}
        </span>

        {showMenu ? (
          <ThreeDotsDropdown
            ref={dropdownRef}
            options={dropdownOptions}
            className="dropdown-on-docs"
            withBorder={false}
            alignment={ALIGNMENT.VERTICAL}
            renderAsPortal
          />
        ) : (
          <span style={{ width: '24px' }} />
        )}
      </span>
    ),
    [
      doesntHaveAccess,
      isFolder,
      areInsightsShown,
      isAdmin,
      isBuySide,
      isBuySidePreviewMemberViewer,
      buySidePreviewMember,
      downloadInsight,
      viewInsight,
      node,
      nodeSize,
      modifiedAt,
      isSelectedMode,
      showMenu,
      viewedDate,
      dropdownOptions,
      phoneModal?.open,
      showNewTag,
      showMobileOptions,
      version,
      directChildrenIds,
      isStarred,
      isStarring,
      hasNewStarringFeature,
      t,
      pastTime,
      dropdownRef,
      updateNode,
      handleStarDocument,
      closeDiscoveringFeature,
    ],
  );

  const updateNodes = useCallback(
    (dataRoomNodes: DataRoomNode[]) => {
      setCurrentlyDisplayedNodes((prev) =>
        prev.map((node) => {
          const foundNode = dataRoomNodes.find(({ _id }) => _id === node._id);

          if (!foundNode) return node;

          return {
            ...node,
            ...foundNode,
          };
        }),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const handleOnEditName = useCallback(
    (e) => {
      e.stopPropagation();

      if (!isEditable) return;

      setIsEditing(true);
    },
    [isEditable],
  );

  const handleOnClick = useCallback(
    (e) => {
      if (isPhablet) {
        if (isSelectedMode) {
          onSelect?.(node);
        } else {
          handleOpen();
        }

        return;
      }

      const isNameClicked = e.target.id === NAME_ID;

      if (isNameClicked) {
        handleOpen();
      } else {
        onSelect?.(node);
      }
    },
    [isPhablet, isSelectedMode, node, onSelect, handleOpen],
  );

  const handleOnDoubleClick = useCallback(
    (e) => {
      if (isPhablet) return;

      if (isSelected) handleOnEditName(e);
    },
    [isPhablet, isSelected, handleOnEditName],
  );

  useEffect(() => {
    const handleRightClicked = () => dropdown?.setIsOpen(false);

    window.addEventListener('contextmenu', handleRightClicked);

    return () => window.removeEventListener('contextmenu', handleRightClicked);
  }, [dropdown]);

  return (
    <div {...restOfRootProps}>
      <input {...getInputProps()} />
      <RightClickDropdown
        options={dropdownOptions}
        disabled={rightClickDisabled}
      >
        <div className={classes}>
          {isEditing ? (
            <EditableDocument
              document={document}
              name={name}
              id={_id}
              setLoading={setIsLoading}
              onCloseEdit={() => setIsEditing(false)}
            />
          ) : (
            <>
              <Button
                onClick={handleOnClick}
                onDoubleClick={handleOnDoubleClick}
                {...(isPhablet && {
                  onTouchStart: handleTouchStart,
                  onTouchEnd: handleTouchEnd,
                  onTouchMove: handleTouchEnd,
                })}
                {...(!isPhablet && {
                  onMouseDown,
                  onMouseUp,
                })}
              >
                {!isPhablet && hasIndexGenerated ? (
                  showNodeStatusBadge || (docIndex && !hasInvalidDocIndex) ? (
                    <ResolvePopup
                      onHover
                      popupContent={
                        <span onClick={(e) => copyToClipboard(e, docIndex)}>
                          {showNodeStatusBadge
                            ? hasFilesFailedConversionToPdf
                              ? t('folderWithFilesAvailableInOriginalFormat')
                              : failedConversionToPdf
                              ? t('failedToConvertToPdf')
                              : t('fileAvailableInOriginalFormat')
                            : docIndex}
                        </span>
                      }
                    >
                      <span className="index">
                        {showNodeStatusBadge ? (
                          hasFilesFailedConversionToPdf ||
                          failedConversionToPdf ? (
                            <XcircleIcon />
                          ) : (
                            <WarningExclamationIcon />
                          )
                        ) : (
                          docIndex
                        )}
                      </span>
                    </ResolvePopup>
                  ) : (
                    <span className="index missing">
                      <DotshorizontalIcon />
                    </span>
                  )
                ) : null}

                {isPhablet && isSelectedMode ? (
                  <div className="checkbox-wrapper">
                    <Checkbox
                      onChange={() => onSelect?.(node)}
                      checked={isSelected}
                    />
                  </div>
                ) : (
                  <MymeTypeIcon mimeType={getNodeMimeType(node)} />
                )}

                <div className="node-info">
                  <div>
                    <TagDocumentBanner
                      tag={suggestedTag}
                      node={node}
                      anchor={
                        <span id={NAME_ID}>
                          {isPhablet &&
                          hasIndexGenerated &&
                          docIndex &&
                          !hasInvalidDocIndex
                            ? `${docIndex}. ${name}`
                            : name}
                        </span>
                      }
                      updateDataRoomNode={updateNode}
                      onSuccessTag={onTagSet}
                      onTagAnotherFile={openTagNodeDrawer}
                    />

                    <DataRoomNodeTagAndVersion tag={tag} version={version} />

                    {isUploading && (
                      <span className="uploading-text">
                        {t('uploading')}...
                      </span>
                    )}
                  </div>

                  <span className="only-show-on-mobile">
                    {buySidePreviewMember ? (
                      <span className="mobile-member-role">
                        {isBuySidePreviewMemberViewer ? 'view' : 'download'}
                      </span>
                    ) : (
                      isAdmin && <span>{numberOfMembersWithAccessCopy}</span>
                    )}

                    {isFolder && (
                      <span>
                        {t('numberOfFiles', {
                          count: directChildrenIds?.length,
                        })}
                      </span>
                    )}

                    {!!nodeSize && (
                      <span>{utils.humanizeFileSize(nodeSize)}</span>
                    )}

                    <span>{pastTime(modifiedAt)}</span>

                    {!isFolder &&
                      areInsightsShown &&
                      (isAdmin || buySidePreviewMember) && (
                        <span className="insights">
                          {downloadInsight}
                          {viewInsight}
                        </span>
                      )}
                  </span>
                </div>
              </Button>

              {RightSideMenu}
            </>
          )}

          {(isRequestPending || isLoading) && (
            <div
              className="loading-overlay"
              onClick={(ev) => ev.stopPropagation()}
            />
          )}
        </div>

        {isUserAuthorized && (
          <>
            <RenameModal node={node} ref={renameModalRef} />
            <DrNodeTagDrawer
              ref={tagThisFileDrawerRef}
              onSuccessTag={onTagSet}
              node={node}
              removeTagFromOldNode={removeTagFromOldNode}
            />
            <MoveDocument nodes={nodes} ref={moveTheFileRef} />
            <ManageFileAccess
              ref={manageFilesAccessRef}
              nodes={nodes}
              parentId={nodes[0]?.parentId}
              updateNodes={updateNodes}
            />
          </>
        )}

        {showMobileOptions && (
          <DataRoomDocumentMobileModal
            ref={phoneModalRef}
            node={node}
            canTagFiles={canTagFiles}
            canBuyerAskQuestion={canBuyerAskQuestion}
            canUserCreateShareableLinks={canUserCreateShareableLinks}
            onMove={() => moveTheFile?.open()}
            onManage={() => manageFilesAccess?.setOpen(true)}
            onRename={() => renameModal?.open()}
            onTag={() => openTagNodeDrawer()}
            onDelete={() => onDocDelete([node], setIsRequestPending)}
            onDownload={() => onDownload(node)}
            onAskQuestion={() => onQandA()}
            onShareFiles={() => onSecureShare()}
          />
        )}
      </RightClickDropdown>
    </div>
  );
};

export default DataRoomDocument;
